import React, { useEffect, useState } from "react";
import { Button, Divider, Grid, Typography } from "@mui/material";
import CustomFormLabel from "src/components/forms/theme-elements/CustomFormLabel";
import ParentCard from "src/components/shared/ParentCard";
import Breadcrumb from "src/layouts/full/shared/breadcrumb/Breadcrumb";
import { useFormik } from "formik";
import * as yup from "yup";
import { useNavigate, useParams } from "react-router";
import CustomTextField from "src/components/forms/theme-elements/CustomTextField";
import { IconArrowBack, IconPlus } from "@tabler/icons";
import {
  Autocomplete,
  GoogleMap,
  Marker,
  DirectionsRenderer,
} from "@react-google-maps/api";
import swal from "sweetalert";
import RouteService from "src/service/route";
import { DeleteOutlineOutlined } from "@mui/icons-material";

const BCrumb = [
  {
    to: "/",
    title: "Home",
  },
  {
    title: " Route Setup ",
  },
];

const validationSchema = yup.object({
  route_name: yup.string("").required("Route Name is required"),
});

const UpdateNewRoute = () => {
  const sessionData = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();

  const [starting_place_name, setStarting_place_name] = useState("");
  const [starting_latitude, setStarting_latitude] = useState("");
  const [starting_longitude, setStarting_longitude] = useState("");

  const [ending_place_name, setEnding_place_name] = useState("");
  const [ending_latitude, setEnding_latitude] = useState("");
  const [ending_longitude, setEnding_longitude] = useState("");

  const [viaPoints, setViaPoints] = useState([]);

  const [distance, setDistance] = useState("");
  const [time_duration, setDuration] = useState("");
  const [directions, setDirections] = useState(null);

  const [startMarker, setStartMarker] = useState({
    position: { lat: 23.876536, lng: 85.876453 },
    address: "",
  });

  const [endMarker, setEndMarker] = useState({
    position: { lat: 23.876536, lng: 85.876453 },
    address: "",
  });

  const param = useParams();

  const [initialValues, setInitialValues] = useState({
    id: param.id,
    route_name: "",
    start_pt_name: "",
    start_pt_latitude: "",
    start_pt_longitude: "",
    end_pt_name: "",
    end_pt_latitude: "",
    end_pt_longitude: "",
    distance: "",
    time_duration: "",
    d_by: sessionData.data.id,
    route_via: [],
  });

  useEffect(() => {
    const fetchRouteDetails = async () => {
      try {
        const res = await RouteService.fullRouteList({
          id: param.id,
        });

        const routeData = res.data[0];

        // Extracting via points data
        const viaPointsData = routeData.vias.map((via) => ({
          via_name: via.via_name,
          via_latitude: via.via_latitude,
          via_longitude: via.via_longitude,
        }));

        // Update the starting point state variables
        setStarting_place_name(routeData.start_pt_name);
        setStarting_latitude(routeData.start_pt_latitude);
        setStarting_longitude(routeData.start_pt_longitude);

        // Update the ending point state variables
        setEnding_place_name(routeData.end_pt_name);
        setEnding_latitude(routeData.end_pt_latitude);
        setEnding_longitude(routeData.end_pt_longitude);

        // Update the distance and time_duration state variables
        setDistance(routeData.distance);
        setDuration(routeData.time_duration);

        setStartMarker({
          position: {
            lat: parseFloat(routeData.start_pt_latitude),
            lng: parseFloat(routeData.start_pt_longitude),
          },
          address: "",
        });

        setEndMarker({
          position: {
            lat: parseFloat(routeData.end_pt_latitude),
            lng: parseFloat(routeData.end_pt_longitude),
          },

          address: "",
        });

        // Update the initialValues with the fetched data
        setInitialValues({
          id: param.id,
          route_name: routeData.route_name,
          start_pt_name: routeData.start_pt_name,
          start_pt_latitude: routeData.start_pt_latitude,
          start_pt_longitude: routeData.start_pt_longitude,
          end_pt_name: routeData.end_pt_name,
          end_pt_latitude: routeData.end_pt_latitude,
          end_pt_longitude: routeData.end_pt_longitude,
          distance: routeData.distance,
          time_duration: routeData.time_duration,
          d_by: sessionData.data.id,
          route_via: viaPointsData,
        });

        // Update via points state
        setViaPoints(viaPointsData);
      } catch (error) {
        console.log(error);
      }
    };
    fetchRouteDetails();
  }, [param.id, sessionData.data.id, sessionData.data.user_type]);

  const onAutocompleteLoadStart = (autocomplete) => {
    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();

      if (place.geometry) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        setStartMarker({
          position: { lat, lng },
          address: place.formatted_address,
        });

        // Update the starting point state variables
        setStarting_place_name(place.formatted_address);
        setStarting_latitude(lat);
        setStarting_longitude(lng);
      }
    });
  };

  const handleStartingPlaceChange = (value) => {
    setStarting_place_name(value);
  };

  const handleEndingPlaceChange = (value) => {
    setEnding_place_name(value);
  };

  const onAutocompleteLoadVia = (autocomplete, index) => {
    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();

      if (place.geometry) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        setViaPoints((prevViaPoints) => {
          const updatedViaPoints = [...prevViaPoints];
          updatedViaPoints[index] = {
            ...updatedViaPoints[index],
            via_latitude: lat,
            via_longitude: lng,
            address: place.formatted_address,
            via_name: place.formatted_address,
          };
          return updatedViaPoints;
        });
      }
    });
  };

  const onAutocompleteLoadEnd = (autocomplete) => {
    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();

      if (place.geometry) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        setEndMarker({
          position: { lat, lng },
          address: place.formatted_address,
        });

        setEnding_place_name(place.formatted_address);
        setEnding_latitude(lat);
        setEnding_longitude(lng);
      }
    });
  };

  useEffect(() => {
    if (startMarker.position.lat && endMarker.position.lat) {
      setTimeout(() => {
        calculateDirections();
      }, [1000]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startMarker, endMarker, viaPoints, param.id]);

  const addViaPoint = () => {
    setViaPoints([
      ...viaPoints,
      { via_name: "", via_latitude: "", via_longitude: "" },
    ]);
  };

  const handleViaPointChange = (index, action, fieldName, newValue) => {
    if (action === "delete") {
      // Remove the via point at the specified index
      const updatedViaPoints = [...viaPoints];
      updatedViaPoints.splice(index, 1);
      setViaPoints(updatedViaPoints);
    } else if (action === "update") {
      // Create a new array and update the via point with the new value
      setViaPoints((prevViaPoints) =>
        prevViaPoints.map((viaPoint, i) =>
          i === index ? { ...viaPoint, [fieldName]: newValue } : { ...viaPoint }
        )
      );
    }
  };

  const calculateDirections = () => {
    const directionsService = new window.google.maps.DirectionsService();
    let totalDistance = 0;
    let totalDuration = 0;

    const waypoints = [];

    // Collect waypoints
    for (let i = 0; i < viaPoints.length; i++) {
      waypoints.push({
        location: new window.google.maps.LatLng(
          parseFloat(viaPoints[i].via_latitude),
          parseFloat(viaPoints[i].via_longitude)
        ),
        stopover: true,
      });
    }

    // Request directions with waypoints
    directionsService.route(
      {
        origin: startMarker.position,
        destination: endMarker.position,
        travelMode: window.google.maps.TravelMode.DRIVING,
        waypoints: waypoints,
      },

      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          setDirections(result);
          // Calculate total distance
          result.routes[0].legs.forEach((leg) => {
            totalDistance += leg.distance.value;
            totalDuration += leg.duration.value;
          });
          setDistance((totalDistance / 1000).toFixed(2) + " km");
          setDuration((totalDuration / 60).toFixed(0));
        } else {
          console.error("Directions request failed due to " + status);
          setDistance(0);
          setDuration(0);
        }
      }
    );
  };

  const { values, errors, touched, handleChange, handleBlur, handleSubmit } =
    useFormik({
      enableReinitialize: true,
      initialValues: initialValues,
      validationSchema: validationSchema,
      onSubmit: async (values) => {
        const viaPointsData = viaPoints.map((viaPoint) => ({
          via_name: viaPoint.via_name,
          via_latitude: viaPoint.via_latitude,
          via_longitude: viaPoint.via_longitude,
        }));
        const payloadval = {
          id: param.id,
          route_name: values.route_name,
          start_pt_name: starting_place_name,
          start_pt_latitude: starting_latitude,
          start_pt_longitude: starting_longitude,
          end_pt_name: ending_place_name,
          end_pt_latitude: ending_latitude,
          end_pt_longitude: ending_longitude,
          distance: parseFloat(distance.replace(/[, ]/g, "")),
          time_duration: time_duration,
          d_by: sessionData.data.id,
          route_via: viaPointsData,
        };

        try {
          const res = await RouteService.updateRouteandVia(payloadval);

          swal("success", res.message, "success");

          setTimeout(() => {
            navigate("/route");
          }, 1000);
        } catch (error) {
          swal("error", error, "error");
        }
      },
    });

  return (
    <>
      <Breadcrumb title="Route" items={BCrumb} />
      <ParentCard title="" style={{ minHeight: "600px !important" }}>
        <Grid container alignItems="center">
          <Grid
            item
            xs={4}
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              gap: 2,
              marginBottom: "15px;",
            }}
          >
            <Typography
              style={{
                fontSize: "22px",
                fontWeight: "bold",
                fontFamily: "Sans-Serif",
              }}
              variant="h2"
            >
              Update Route
            </Typography>
          </Grid>

          <Grid
            item
            xs={8}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              marginBottom: "15px;",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                navigate("/route");
              }}
            >
              <IconArrowBack width={20} />
            </Button>
          </Grid>
        </Grid>
        <Divider />
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3} style={{ marginTop: "10px" }}>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Grid item spacing={3} container lg={12} md={12} sm={12} xs={12}>
                <Grid
                  item
                  lg={4}
                  md={6}
                  sm={6}
                  xs={12}
                  style={{ marginTop: "-10px" }}
                >
                  <CustomFormLabel htmlFor="start_pt_latitude">
                    Start Place Name <span style={{ color: "red" }}>*</span>
                  </CustomFormLabel>

                  <Autocomplete onLoad={onAutocompleteLoadStart}>
                    <CustomTextField
                      type="text"
                      value={starting_place_name}
                      onChange={(event, newValue) => {
                        handleChange(event);
                        handleStartingPlaceChange(newValue);
                      }}
                      onBlur={(event, newValue) => handleBlur(event)}
                      variant="outlined"
                      placeholder="Enter starting address"
                      fullWidth
                    />
                  </Autocomplete>
                </Grid>

                <Grid
                  item
                  lg={4}
                  md={6}
                  sm={6}
                  xs={12}
                  style={{ marginTop: "-10px" }}
                >
                  <CustomFormLabel htmlFor="start_pt_latitude">
                    Latitude <span style={{ color: "red" }}>*</span>
                  </CustomFormLabel>
                  <CustomTextField
                    type="text"
                    value={starting_latitude}
                    variant="outlined"
                    // placeholder="Enter Starting Destination Latitude"
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                </Grid>

                <Grid
                  item
                  lg={4}
                  md={6}
                  sm={6}
                  xs={12}
                  style={{ marginTop: "-10px" }}
                >
                  <CustomFormLabel htmlFor="start_pt_longitude">
                    Longitude <span style={{ color: "red" }}>*</span>
                  </CustomFormLabel>
                  <CustomTextField
                    id="start_pt_longitude"
                    name="start_pt_longitude"
                    type="text"
                    value={starting_longitude}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    variant="outlined"
                    // placeholder="Enter Starting Destination Longitude"
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                </Grid>

                {/* ===================== via points code ===================== */}

                {viaPoints.map((viaPoint, index) => (
                  <React.Fragment key={index}>
                    <Grid
                      item
                      lg={4}
                      md={6}
                      sm={6}
                      xs={12}
                      style={{ marginTop: "-10px" }}
                    >
                      <CustomFormLabel htmlFor={`via_name_${index}`}>
                        Via Point Name
                      </CustomFormLabel>
                      <Autocomplete
                        onLoad={(autocomplete) =>
                          onAutocompleteLoadVia(autocomplete, index)
                        }
                      >
                        <CustomTextField
                          id={`via_name_${index}`}
                          name={`via_name_${index}`}
                          type="text"
                          value={viaPoint.via_name}
                          onChange={(e, newValue) =>
                            handleViaPointChange(
                              index,
                              "update",
                              "via_name",
                              newValue
                            )
                          }
                          variant="outlined"
                          placeholder="Enter Via Point Name"
                          fullWidth
                        />
                      </Autocomplete>
                    </Grid>

                    <Grid
                      item
                      lg={3}
                      md={6}
                      sm={6}
                      xs={12}
                      style={{ marginTop: "-10px" }}
                    >
                      <CustomFormLabel htmlFor={`via_latitude_${index}`}>
                        Via Point Latitude
                      </CustomFormLabel>
                      <CustomTextField
                        id={`via_latitude_${index}`}
                        name={`via_latitude_${index}`}
                        type="text"
                        value={viaPoint.via_latitude}
                        onChange={(e) =>
                          handleViaPointChange(e, index, "via_latitude")
                        }
                        variant="outlined"
                        placeholder="Via Point Latitude"
                        fullWidth
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>

                    <Grid
                      item
                      lg={3}
                      md={6}
                      sm={6}
                      xs={12}
                      style={{ marginTop: "-10px" }}
                    >
                      <CustomFormLabel htmlFor={`via_longitude_${index}`}>
                        Via Point Longitude
                      </CustomFormLabel>
                      <CustomTextField
                        id={`via_longitude_${index}`}
                        name={`via_longitude_${index}`}
                        type="text"
                        value={viaPoint.via_longitude}
                        onChange={(e) =>
                          handleViaPointChange(e, index, "via_longitude")
                        }
                        variant="outlined"
                        placeholder="Via Point Longitude"
                        fullWidth
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>

                    <Grid
                      item
                      lg={2}
                      md={2}
                      sm={2}
                      xs={2}
                      style={{ marginTop: "15px" }}
                    >
                      {/* Add a button to delete the via point */}
                      <Button
                        variant="outlined"
                        onClick={() => handleViaPointChange(index, "delete")}
                        style={{ marginTop: "10px", color: "red" }}
                      >
                        <DeleteOutlineOutlined />
                      </Button>
                    </Grid>
                  </React.Fragment>
                ))}

                <Grid item lg={12} md={12} sm={12}>
                  <Button
                    variant="outlined"
                    onClick={addViaPoint}
                    style={{ float: "right" }}
                  >
                    <IconPlus />
                  </Button>
                </Grid>
                {/* ===================== via points code ends ================ */}

                <Grid
                  item
                  lg={4}
                  md={6}
                  sm={6}
                  xs={12}
                  style={{ marginTop: "-10px" }}
                >
                  <CustomFormLabel htmlFor="end_pt_name">
                    End Place Name <span style={{ color: "red" }}>*</span>
                  </CustomFormLabel>
                  <Autocomplete onLoad={onAutocompleteLoadEnd}>
                    <CustomTextField
                      id="end_pt_name"
                      name="end_pt_name"
                      type="text"
                      value={ending_place_name}
                      onChange={(event, newValue) => {
                        handleChange(event);
                        handleEndingPlaceChange(newValue);
                      }}
                      onBlur={(event, newValue) => {
                        handleBlur(event);
                      }}
                      variant="outlined"
                      placeholder="Enter End address"
                      fullWidth
                    />
                  </Autocomplete>
                </Grid>

                <Grid
                  item
                  lg={4}
                  md={6}
                  sm={6}
                  xs={12}
                  style={{ marginTop: "-10px" }}
                >
                  <CustomFormLabel htmlFor="end_pt_latitude">
                    Latitude <span style={{ color: "red" }}>*</span>
                  </CustomFormLabel>
                  <CustomTextField
                    id="end_pt_latitude"
                    name="end_pt_latitude"
                    type="text"
                    value={ending_latitude}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    variant="outlined"
                    // placeholder=" Final Destination Latitude"
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                </Grid>

                <Grid
                  item
                  lg={4}
                  md={6}
                  sm={6}
                  xs={12}
                  style={{ marginTop: "-10px" }}
                >
                  <CustomFormLabel htmlFor="end_pt_longitude">
                    Longitude <span style={{ color: "red" }}>*</span>
                  </CustomFormLabel>
                  <CustomTextField
                    id="end_pt_longitude"
                    name="end_pt_longitude"
                    type="text"
                    value={ending_longitude}
                    variant="outlined"
                    // placeholder="Enter Final Destination Longitude"
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </Grid>

              <GoogleMap
                mapContainerStyle={{
                  height: "400px",
                  width: "100%",
                  marginTop: "20px",
                }}
                zoom={10}
                center={startMarker.position}
              >
                {directions && <DirectionsRenderer directions={directions} />}

                <Marker position={startMarker.position} />
                <Marker position={endMarker.position} />

                {viaPoints.map((viaPoint, index) => (
                  <Marker
                    key={index}
                    position={{
                      lat: parseFloat(viaPoint.via_latitude),
                      lng: parseFloat(viaPoint.via_longitude),
                    }}
                  />
                ))}
              </GoogleMap>
            </Grid>

            <Grid
              item
              lg={4}
              md={6}
              sm={6}
              xs={12}
              style={{ marginTop: "-10px" }}
            >
              <CustomFormLabel htmlFor="route_name">
                Route Name <span style={{ color: "red" }}>*</span>
              </CustomFormLabel>
              <CustomTextField
                id="route_name"
                name="route_name"
                type="text"
                value={values.route_name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.route_name && Boolean(errors.route_name)}
                helperText={touched.route_name && errors.route_name}
                variant="outlined"
                placeholder="Enter Route Name"
                fullWidth
              />
            </Grid>

            <Grid
              item
              lg={4}
              md={6}
              sm={6}
              xs={12}
              style={{ marginTop: "-10px" }}
            >
              <CustomFormLabel htmlFor="distance">
                Total Distance (Km) <span style={{ color: "red" }}>*</span>
              </CustomFormLabel>
              <CustomTextField
                id="distance"
                name="distance"
                type="text"
                value={distance}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.distance && Boolean(errors.distance)}
                helperText={touched.distance && errors.distance}
                variant="outlined"
                placeholder="Total Distance"
                fullWidth
              />
            </Grid>

            <Grid
              item
              lg={4}
              md={6}
              sm={6}
              xs={12}
              style={{ marginTop: "-10px" }}
            >
              <CustomFormLabel htmlFor="time_duration">
                Total Duration (Min) <span style={{ color: "red" }}>*</span>
              </CustomFormLabel>
              <CustomTextField
                id="time_duration"
                name="time_duration"
                type="text"
                value={time_duration}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.time_duration && Boolean(errors.time_duration)}
                helperText={touched.time_duration && errors.time_duration}
                variant="outlined"
                placeholder="Total Duration in minute"
                fullWidth
              />
            </Grid>
            <Grid item lg={12} md={12} sm={12}>
              <Button type="submit" color="success" variant="contained">
                Submit
              </Button>
            </Grid>
          </Grid>
        </form>
      </ParentCard>
    </>
  );
};

export default UpdateNewRoute;
